/**
 * The footer a wizard shell draws below its body: an optional Back on the left of the primary, both
 * right-aligned above a top border.
 *
 * ## Why it takes resolved actions, not a `WizardFooterState`
 *
 * `footerState` is a pure function of a step TREE, and two of this component's three hosts have one:
 * `AudienceActivationSheet` and the Settings add-account drawer. The Edit drawer does not — editing
 * an account is a single screen, and giving it a one-node tree to satisfy a prop type would be
 * inventing structure to match a pattern rather than because the flow has it.
 *
 * So the contract is the smallest thing all three can speak: two nullable actions. A host with a tree
 * maps `footerState` onto them (`showBack ? {...} : null`), a host without one passes what it has.
 * That also keeps the label question where it belongs — the sheet's primary reads "Next", "Connect"
 * or "Enable sync" depending on which flow owns the step, which no single `doneLabel` could express.
 *
 * ## Why there is no `show` prop
 *
 * `footerState.showFooter` is exactly `showBack || showPrimary`, so a `show` prop would be a third
 * source of truth that could disagree with the two actions it describes. Both null renders nothing,
 * which is the same rule stated once.
 */
import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { SheetFooter } from '@/_shared/components/ui/sheet';

export interface WizardFooterAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /**
   * Why this action is unavailable, shown on hover while it is disabled (ui skill rule 10 —
   * disable with a reason, never hide). Omit for a disabled state the surrounding screen already
   * explains, such as a primary greyed out while its own form is incomplete.
   */
  disabledReason?: string | null;
}

interface WizardFooterProps {
  back?: WizardFooterAction | null;
  primary?: WizardFooterAction | null;
}

/**
 * One action, wrapped so a reason can appear beside it.
 *
 * `keepMounted` is unconditional, and that is the point: the reasons these buttons carry are
 * TRANSIENT — Back is disabled only while an OAuth consent popup is live — and `ActionTooltip`
 * unwraps to a bare child when `show` goes false unless told otherwise. That unwrap remounts the
 * button, so a keyboard user standing on Back loses focus the instant the popup closes. Always
 * rendering the wrapper costs one span and makes the flip invisible.
 */
function FooterAction({
  action,
  variant,
}: {
  action: WizardFooterAction;
  variant: 'secondary' | 'primary';
}): React.JSX.Element {
  const disabled = action.disabled ?? false;
  return (
    <ActionTooltip show={disabled && !!action.disabledReason} message={action.disabledReason ?? ''} keepMounted>
      <Button
        type="button"
        variant={variant}
        size="sm"
        label={action.label}
        disabled={disabled}
        loading={action.loading ?? false}
        onClick={action.onClick}
      />
    </ActionTooltip>
  );
}

export function WizardFooter({ back = null, primary = null }: WizardFooterProps): React.JSX.Element | null {
  if (!back && !primary) return null;
  return (
    <SheetFooter className="flex-row items-center justify-end gap-2 border-t border-border p-4">
      {back ? <FooterAction action={back} variant="secondary" /> : null}
      {primary ? <FooterAction action={primary} variant="primary" /> : null}
    </SheetFooter>
  );
}
